import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ModelType, IMusicEditing, TaskStatus,
  CreateMusicEditingRequest, MusicEditingQuery, PaginationQuery, PaginationResult,
  MusicEditingTypeLabels, getLabelsByLocale, Locale,
} from '@packages/global';
import { musicEditingApi } from '../services/user/musicEditing.api';
import { useModels } from '../hooks/useModels';
import { useDialog } from '../hooks/useDialog';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { MessageType, TaskStatusChangedMessage, TaskType } from '../services/websocket/types';
import { MagnifyingGlassIcon, MusicalNoteIcon } from '@heroicons/react/24/outline';
import MusicTabs from '../components/common/MusicTabs';
import MusicEditingForm, { MusicEditFormData, DEFAULT_EDIT_FORM } from '../components/MusicEditing/MusicEditingForm';
import MusicEditCard from '../components/MusicEditing/MusicEditCard';
import Pagination from '../components/Pagination';

export default function MusicEditsPage() {
  const { t, i18n } = useTranslation(['generation', 'common']);
  const [items, setItems] = useState<IMusicEditing[]>([]);
  const [loading, setLoading] = useState(true);
  const { models, loading: modelsLoading } = useModels(ModelType.MUSIC_EDIT);
  const { alert, confirm } = useDialog();
  const { client, isConnected } = useWebSocketContext();

  const [formData, setFormData] = useState<MusicEditFormData>(DEFAULT_EDIT_FORM);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');
  const [editingTypeFilter, setEditingTypeFilter] = useState('');
  const [pagination, setPagination] = useState<PaginationResult>({
    page: 1, limit: 24, total: 0, totalPages: 0,
    sortBy: 'createdAt', sortOrder: 'desc',
  });

  const EDITING_TYPE_OPTIONS = [
    { value: '', label: t('generation:musicEdit.allType') },
    { value: 'song_cover', label: t('generation:musicEdit.form.typeSongCover') },
    { value: 'cover', label: t('generation:musicEdit.form.typeCover') },
    { value: 'repaint', label: t('generation:musicEdit.form.typeRepaint') },
    { value: 'separate', label: t('generation:musicEdit.form.typeSeparate') },
  ];

  const STATUS_OPTIONS = [
    { value: '', label: t('generation:musicEdit.allStatus') },
    { value: TaskStatus.PENDING, label: t('generation:status.pending') },
    { value: TaskStatus.SUBMITTED, label: t('generation:status.submitted') },
    { value: TaskStatus.PROCESSING, label: t('generation:status.processing') },
    { value: TaskStatus.COMPLETED, label: t('generation:status.completed') },
    { value: TaskStatus.FAILED, label: t('generation:status.failed') },
  ];

  const loadItems = async (
    queryParams: Partial<MusicEditingQuery> = {},
    page = 1,
    limit = 24
  ) => {
    try {
      const queryData: PaginationQuery<MusicEditingQuery> = {
        query: {
          search: queryParams.search ?? searchTerm,
          status: queryParams.status ?? (statusFilter || undefined),
          editingType: (queryParams.editingType ?? editingTypeFilter) as any || undefined,
          ...queryParams,
        },
        pagination: { page, limit, sortBy: 'createdAt', sortOrder: 'desc' },
      };
      const response = await musicEditingApi.page(queryData);
      setItems(response.items);
      setPagination(prev => ({
        ...prev, page, limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
      }));
    } catch (error) {
      console.error('加载音乐编辑任务失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  // WebSocket 订阅
  useEffect(() => {
    if (!client || !isConnected) return;
    const unsubStatus = client.onMessage(MessageType.TASK_STATUS_CHANGED, (msg: TaskStatusChangedMessage) => {
      if (msg.taskType !== TaskType.MUSIC_EDITING) return;
      setItems(prev => {
        const idx = prev.findIndex(i => i.id === msg.taskId);
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], status: msg.newStatus as unknown as TaskStatus };
        if (msg.newStatus === TaskStatus.COMPLETED || msg.newStatus === TaskStatus.FAILED) {
          musicEditingApi.get(msg.taskId)
            .then(updated => setItems(p => p.map(i => i.id === updated.id ? updated : i)))
            .catch(console.error);
        }
        return next;
      });
    });

    const unsubCompleted = client.onMessage(MessageType.TASK_COMPLETED, (msg: any) => {
      if (msg.taskType !== TaskType.MUSIC_EDITING) return;
      musicEditingApi.get(msg.taskId)
        .then(updated => setItems(p => p.map(i => i.id === updated.id ? updated : i)))
        .catch(console.error);
    });

    const unsubFailed = client.onMessage(MessageType.TASK_FAILED, (msg: any) => {
      if (msg.taskType !== TaskType.MUSIC_EDITING) return;
      musicEditingApi.get(msg.taskId)
        .then(updated => setItems(p => p.map(i => i.id === updated.id ? updated : i)))
        .catch(console.error);
    });

    return () => { unsubStatus(); unsubCompleted(); unsubFailed(); };
  }, [client, isConnected]);

  const handleSubmit = async (data: CreateMusicEditingRequest) => {
    try {
      const newItem = await musicEditingApi.create(data);

      // 自动填充标题：编辑类型 + 时间
      const localizedLabels = getLabelsByLocale(MusicEditingTypeLabels, i18n.language as Locale);
      const typeLabel = localizedLabels[data.editingType] ?? data.editingType;
      const autoTitle = `${typeLabel} ${new Date().toLocaleString(i18n.language, { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}`;
      musicEditingApi.updateTitle(newItem.id, autoTitle).catch(console.error);
      newItem.title = autoTitle;

      setItems(prev => [newItem, ...prev]);
      setPagination(prev => ({ ...prev, total: prev.total + 1 }));
    } catch (error: any) {
      await alert(error.response?.data?.message || t('generation:musicEdit.createFailed'), t('generation:musicEdit.alertError'), 'danger');
      throw error;
    }
  };

  const handleReuseParams = (item: IMusicEditing) => {
    setFormData({
      editingType: item.editingType,
      modelId: item.modelId,
      srcAudioUrl: item.srcAudioUrl ?? '',
      referenceAudioUrl: item.referenceAudioUrl ?? '',
      audioCoverStrength: item.audioCoverStrength ?? 0.95,
      coverNoiseStrength: item.coverNoiseStrength ?? 0.25,
      repaintingStart: item.repaintingStart ?? '',
      repaintingEnd: item.repaintingEnd ?? '',
      rvcModelId: (item as any).rvcModelId ?? '',
      pitch: (item as any).pitch ?? 0,
      indexRate: (item as any).indexRate ?? 0.6,
      vocalGainDb: (item as any).vocalGainDb ?? 0,
      instrumentalGainDb: (item as any).instrumentalGainDb ?? 0,
      cleanAudio: (item as any).cleanAudio ?? false,
      f0Autotune: (item as any).f0Autotune ?? false,
      uvrModel: '',
      enableBackingVocalRemoval: (item as any).enableBackingVocalRemoval ?? true,
      outputFormat: '',
      trackName: 'vocals',
    });
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm(t('generation:musicEdit.deleteConfirm'), t('common:confirmDelete'), 'danger');
    if (!confirmed) return;
    try {
      await musicEditingApi.delete(id);
      loadItems({}, pagination.page, pagination.limit);
    } catch (error) {
      console.error('删除失败:', error);
    }
  };

  return (
    <div className="flex h-screen bg-bg-page">
      {/* 左侧表单 */}
      <div className="w-96 lg:w-[420px] bg-bg-form border-r border-border-subtle shadow-xl flex flex-col">
        <MusicTabs />
        <MusicEditingForm
          formData={formData}
          models={models}
          modelsLoading={modelsLoading}
          onFormDataChange={(data) => setFormData(prev => ({ ...prev, ...data }))}
          onSubmit={handleSubmit}
        />
      </div>

      {/* 右侧任务列表 */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* 标题 + 筛选 */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-50">{t('generation:musicEdit.title')}</h1>
              <p className="mt-1 text-sm text-gray-400">{t('generation:musicEdit.subtitle')}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('generation:musicEdit.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    loadItems({ search: e.target.value }, 1);
                  }}
                  className="block w-full pl-9 pr-3 py-2 border border-border-subtle rounded-lg bg-bg-form placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-gray-50 text-sm"
                />
              </div>
              <select
                value={editingTypeFilter}
                onChange={(e) => {
                  setEditingTypeFilter(e.target.value);
                  loadItems({ editingType: e.target.value as any || undefined }, 1);
                }}
                className="px-3 py-2 border border-border-subtle rounded-lg bg-bg-form text-gray-50 text-sm focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
              >
                {EDITING_TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => {
                  const v = e.target.value as TaskStatus | '';
                  setStatusFilter(v);
                  loadItems({ status: v || undefined }, 1);
                }}
                className="px-3 py-2 border border-border-subtle rounded-lg bg-bg-form text-gray-50 text-sm focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 列表 */}
          {loading ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle p-12 flex justify-center items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <span className="ml-3 text-gray-400">{t('common:loading')}</span>
            </div>
          ) : items.length === 0 ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle p-12 text-center">
              <MusicalNoteIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">{t('generation:musicEdit.empty')}</p>
              <p className="text-gray-500 text-sm mt-1">{t('generation:musicEdit.emptyHint')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {items.map(item => (
                <MusicEditCard
                  key={item.id}
                  item={item}
                  onDelete={handleDelete}
                  onReuse={handleReuseParams}
                />
              ))}
            </div>
          )}

          {pagination.totalPages > 1 && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 mt-6">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={(page) => loadItems({}, page, pagination.limit)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
